import { useRef, useState } from 'react';

import { NOTICES_PAGE } from '@/shared/notices/api';
import type { Notice } from '@/shared/notices/types';
import {
  useMarkNoticesSeen,
  useNoticeList,
  useNoticesAllowed,
  usePendingNotices,
  useWithdrawNotice,
} from '@/shared/notices/useNotices';
import { isManager, useSessionUser } from '@/shared/session/currentUser';
import { Button } from '@/shared/ui/Button';
import { Dialog } from '@/shared/ui/Dialog';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { BellIcon, PlusIcon } from '@/shared/ui/icons';
import { Spinner } from '@/shared/ui/Spinner';

import { NoticeItem } from './NoticeItem';
import { WriteNotice } from './WriteNotice';

const COUNT = new Intl.NumberFormat('es-ES');

/** The most one page of the API gives: past it, the oldest stay out of the bell. */
const NOTICES_MAX = 200;

/** «9+» past nine: the bell says there is something, the list says what. */
function badge(unread: number): string {
  return unread > 9 ? '9+' : String(unread);
}

/**
 * The bell (contract v10.50): how many notices this person has not seen, and every notice
 * under it, newest first. `place` is where it hangs: a row of the sidebar on a computer, an
 * icon in the thin bar on a phone. Both read the same query.
 */
export function NoticeBell({ place }: { place: 'sidebar' | 'header' }) {
  const allowed = useNoticesAllowed();
  const pending = usePendingNotices();
  const [open, setOpen] = useState(false);
  const unread = pending.data?.unread ?? 0;
  const label = unread > 0 ? `Avisos: ${COUNT.format(unread)} sin ver` : 'Avisos';
  // Until the person changes the password they were given, the API answers nothing else.
  if (!allowed) return null;

  return (
    <>
      {place === 'sidebar' ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-label={label}
          className="text-nav-text group flex h-9 w-full items-center gap-2.5 rounded-md px-3 text-[14px] font-medium transition-colors hover:bg-white/5 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70"
        >
          <span className="text-nav-text shrink-0 group-hover:text-white">
            <BellIcon />
          </span>
          <span className="truncate">Avisos</span>
          {unread > 0 ? (
            <span
              aria-hidden="true"
              className="bg-brand ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11.5px] font-semibold text-white"
            >
              {badge(unread)}
            </span>
          ) : null}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-label={label}
          title={label}
          className="relative inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-white/5 text-white/90"
        >
          <BellIcon size={17} />
          {unread > 0 ? (
            <span
              aria-hidden="true"
              className="bg-brand ring-nav absolute -top-1 -right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10.5px] font-semibold text-white ring-2"
            >
              {badge(unread)}
            </span>
          ) : null}
        </button>
      )}
      {open ? <NoticesDialog onClose={() => setOpen(false)} /> : null}
    </>
  );
}

/**
 * Every notice not withdrawn this person may see. Whoever administers writes one from here,
 * sees every one with whom it goes to (contract v10.55, also the ones that are not for
 * them) and withdraws any. Closing it counts as seen for the new ones it showed, like the
 * window. New is what the back says the window would show (`new`): an expired one, one from
 * before this person had an account, or one for others, is not new even if they never
 * opened it.
 */
function NoticesDialog({ onClose }: { onClose: () => void }) {
  const user = useSessionUser();
  const manager = isManager(user);
  const [limit, setLimit] = useState(NOTICES_PAGE);
  const [writing, setWriting] = useState(false);
  const list = useNoticeList(true, limit);
  const seen = useMarkNoticesSeen();
  const shownUnread = useRef(new Set<string>());

  const items = list.data?.items ?? [];
  for (const notice of items) {
    if (notice.new) shownUnread.current.add(notice.id);
  }

  function close() {
    const ids = [...shownUnread.current];
    if (ids.length > 0) seen.mutate(ids);
    onClose();
  }

  if (writing) {
    return <WriteNotice onDone={() => setWriting(false)} />;
  }

  const total = list.data?.total ?? 0;
  return (
    <Dialog
      title="Avisos"
      description="Lo que se ha avisado al equipo, lo último arriba."
      onClose={close}
      size="md"
      footer={
        manager ? (
          <Button variant="secondary" onClick={() => setWriting(true)}>
            <PlusIcon size={16} />
            Escribir un aviso
          </Button>
        ) : undefined
      }
    >
      {list.isPending ? (
        <Spinner label="Cargando los avisos" />
      ) : list.isError ? (
        <ErrorMessage error={list.error}>No se pudieron cargar los avisos</ErrorMessage>
      ) : items.length === 0 ? (
        <EmptyState
          title="Ningún aviso"
          icon={<BellIcon />}
          hint={
            manager
              ? 'Lo que escribas aquí le saldrá al entrar a todo el equipo o a quien elijas. Al publicar una tarifa sale uno solo.'
              : 'Cuando haya algo que el equipo tenga que saber, saldrá aquí.'
          }
        />
      ) : (
        <div className="flex flex-col gap-3">
          <ul className="divide-line-2 flex flex-col divide-y">
            {items.map((notice) => (
              <li key={notice.id} className="py-3 first:pt-0">
                <NoticeItem
                  notice={notice}
                  fresh={Boolean(notice.new)}
                  showAudience
                  onFollow={close}
                  actions={notice.can_withdraw ? <Withdraw notice={notice} /> : null}
                />
              </li>
            ))}
          </ul>
          {total > items.length && limit < NOTICES_MAX ? (
            <div>
              <Button
                variant="secondary"
                size="sm"
                disabled={list.isFetching}
                onClick={() => setLimit((value) => Math.min(value + NOTICES_PAGE, NOTICES_MAX))}
              >
                {list.isFetching ? 'Cargando…' : 'Ver más'}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}

/** «Retirar», with a second click that says for whom: everybody it went to. */
function Withdraw({ notice }: { notice: Notice }) {
  const [asking, setAsking] = useState(false);
  const withdraw = useWithdrawNotice();
  if (!asking) {
    return (
      <button
        type="button"
        onClick={() => setAsking(true)}
        className="text-ink-3 hover:text-danger text-[13px] underline-offset-2 hover:underline"
      >
        Retirar
      </button>
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className="text-ink-2 text-[13px]">
        {notice.audience && !notice.audience.everyone
          ? 'Deja de salir a todos a los que iba.'
          : 'Deja de salir a todo el equipo.'}
      </span>
      <Button
        variant="danger"
        size="sm"
        disabled={withdraw.isPending}
        onClick={() => withdraw.mutate(notice.id)}
      >
        {withdraw.isPending ? 'Retirando…' : 'Retirar para todos'}
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setAsking(false)}>
        Cancelar
      </Button>
      {withdraw.isError ? (
        <ErrorMessage error={withdraw.error} className="w-full">
          No se pudo retirar
        </ErrorMessage>
      ) : null}
    </span>
  );
}
